/* tslint:disable */
/* eslint-disable */
/**
 * FreightSimple Carriers API
 * Carrier-facing API for the carrier portal (carriers.freightsimple.com).  Authentication: a carrier opens a permanent signed login link, exchanges it for a session token, and presents that token as a bearer on every other call. `CarrierPortalAuthenticationFilter` validates the session and scopes each request to one carrier. Operations use `security: []` (no Micronaut JWT) — the filter is the authenticator, the same model the agents API uses.
 *
 * The version of the OpenAPI document: 1.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from "../runtime";
import type { CustomerSpecificPricingStatus } from "./CustomerSpecificPricingStatus";
import {
  CustomerSpecificPricingStatusFromJSON,
  CustomerSpecificPricingStatusFromJSONTyped,
  CustomerSpecificPricingStatusToJSON,
  CustomerSpecificPricingStatusToJSONTyped,
} from "./CustomerSpecificPricingStatus";

/**
 *
 * @export
 * @interface CustomerSpecificPricingAgreement
 */
export interface CustomerSpecificPricingAgreement {
  /**
   * Opaque id, used to open the agreement's detail page.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  agreementId: string;
  /**
   * The customer FreightSimple holds the pricing for.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  companyName: string;
  /**
   * The customer's account name in the carrier's system.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  accountName?: string;
  /**
   * The customer's account number with the carrier.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  accountNumber?: string;
  /**
   * The specific service the pricing applies to, if scoped.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  serviceIdentifier?: string;
  /**
   *
   * @type {CustomerSpecificPricingStatus}
   * @memberof CustomerSpecificPricingAgreement
   */
  status: CustomerSpecificPricingStatus;
  /**
   * Pre-formatted, carrier-friendly label for the status.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  statusLabel: string;
  /**
   * Short description of what's outstanding / whose turn it is.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  nextStep?: string;
  /**
   * True when the ball is in the carrier's court.
   * @type {boolean}
   * @memberof CustomerSpecificPricingAgreement
   */
  needsCarrierAction: boolean;
  /**
   * Pre-formatted date the pricing went live, if it has.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  activatedAt?: string;
  /**
   * Pre-formatted date the pricing expires, if set.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  expiresAt?: string;
  /**
   * Unique shipments the customer asked this carrier to price, deduplicated by
   * shipment_hash per the Metric Glossary's Unique Quotes definition. Quotes the
   * customer never saw (hidden pricing intermediates) are excluded.
   *
   * @type {number}
   * @memberof CustomerSpecificPricingAgreement
   */
  quoteCount?: number;
  /**
   * Shipments the customer booked on this pricing — real shipments, so it stays
   * consistent with the paid totals on the detail page. Not the win-rate
   * denominator's unit; use winRate rather than dividing these two counts.
   *
   * @type {number}
   * @memberof CustomerSpecificPricingAgreement
   */
  bookingCount?: number;
  /**
   * Unique booked shipments divided by unique quoted shipments, 0..1. Computed
   * server-side because both sides must be deduplicated by shipment_hash to be
   * commensurable, which the displayed counts are not.
   *
   * @type {number}
   * @memberof CustomerSpecificPricingAgreement
   */
  winRate?: number;
  /**
   * ISO instant of the most recent quote, for relative display.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  lastQuotedAt?: string;
  /**
   * ISO instant of the most recent booking, for relative display.
   * @type {string}
   * @memberof CustomerSpecificPricingAgreement
   */
  lastBookedAt?: string;
}

/**
 * Check if a given object implements the CustomerSpecificPricingAgreement interface.
 */
export function instanceOfCustomerSpecificPricingAgreement(
  value: object,
): value is CustomerSpecificPricingAgreement {
  if (!("agreementId" in value) || value["agreementId"] === undefined)
    return false;
  if (!("companyName" in value) || value["companyName"] === undefined)
    return false;
  if (!("status" in value) || value["status"] === undefined) return false;
  if (!("statusLabel" in value) || value["statusLabel"] === undefined)
    return false;
  if (
    !("needsCarrierAction" in value) ||
    value["needsCarrierAction"] === undefined
  )
    return false;
  return true;
}

export function CustomerSpecificPricingAgreementFromJSON(
  json: any,
): CustomerSpecificPricingAgreement {
  return CustomerSpecificPricingAgreementFromJSONTyped(json, false);
}

export function CustomerSpecificPricingAgreementFromJSONTyped(
  json: any,
  ignoreDiscriminator: boolean,
): CustomerSpecificPricingAgreement {
  if (json == null) {
    return json;
  }
  return {
    agreementId: json["agreementId"],
    companyName: json["companyName"],
    accountName: json["accountName"] == null ? undefined : json["accountName"],
    accountNumber:
      json["accountNumber"] == null ? undefined : json["accountNumber"],
    serviceIdentifier:
      json["serviceIdentifier"] == null ? undefined : json["serviceIdentifier"],
    status: CustomerSpecificPricingStatusFromJSON(json["status"]),
    statusLabel: json["statusLabel"],
    nextStep: json["nextStep"] == null ? undefined : json["nextStep"],
    needsCarrierAction: json["needsCarrierAction"],
    activatedAt: json["activatedAt"] == null ? undefined : json["activatedAt"],
    expiresAt: json["expiresAt"] == null ? undefined : json["expiresAt"],
    quoteCount: json["quoteCount"] == null ? undefined : json["quoteCount"],
    bookingCount:
      json["bookingCount"] == null ? undefined : json["bookingCount"],
    winRate: json["winRate"] == null ? undefined : json["winRate"],
    lastQuotedAt:
      json["lastQuotedAt"] == null ? undefined : json["lastQuotedAt"],
    lastBookedAt:
      json["lastBookedAt"] == null ? undefined : json["lastBookedAt"],
  };
}

export function CustomerSpecificPricingAgreementToJSON(
  json: any,
): CustomerSpecificPricingAgreement {
  return CustomerSpecificPricingAgreementToJSONTyped(json, false);
}

export function CustomerSpecificPricingAgreementToJSONTyped(
  value?: CustomerSpecificPricingAgreement | null,
  ignoreDiscriminator: boolean = false,
): any {
  if (value == null) {
    return value;
  }

  return {
    agreementId: value["agreementId"],
    companyName: value["companyName"],
    accountName: value["accountName"],
    accountNumber: value["accountNumber"],
    serviceIdentifier: value["serviceIdentifier"],
    status: CustomerSpecificPricingStatusToJSON(value["status"]),
    statusLabel: value["statusLabel"],
    nextStep: value["nextStep"],
    needsCarrierAction: value["needsCarrierAction"],
    activatedAt: value["activatedAt"],
    expiresAt: value["expiresAt"],
    quoteCount: value["quoteCount"],
    bookingCount: value["bookingCount"],
    winRate: value["winRate"],
    lastQuotedAt: value["lastQuotedAt"],
    lastBookedAt: value["lastBookedAt"],
  };
}
